import { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useEpicsPaged } from "@/hooks/queries/use-epics";
import type { EpicFilters, EpicStateFilter } from "@/lib/api/epics";
import { useTeams } from "@/hooks/queries/use-teams";
import { useAuthStore } from "@/stores/auth-store";
import { usePageTitle } from "@/hooks/use-page-title";
import { useIsMobile } from "@/hooks/use-mobile";
import { getInitials } from "@/lib/get-initials";
import { Button } from "@/components/ui/button";

import { EpicCard } from "@/components/epics/epic-card";
import { EpicsScopeFilter } from "@/components/epics/epics-scope-filter";
import type { ScopeMode } from "@/components/common/scope-selector";
import { SortControl } from "@/components/common/sort-control";
import type { SortOption } from "@/components/common/sort-control";
import { PaginationControls, PAGE_SIZE_OPTIONS } from "@/components/common/pagination-controls";
import { getSortPreference, setSortPreference } from "@/lib/sort-preferences";
import { Filter, Check, Folder, List, LayoutGrid, User } from "lucide-react";
import { SmartEmptyState } from "@/components/common/smart-empty-state";
import { PageInfoButton } from "@/components/common/page-info-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Epic } from "@/lib/api/types";
import { getTeamHueColor } from "@/lib/team-hue";

type StateFilterOption = EpicStateFilter | 'all';

const STATE_FILTER_LABELS: Record<StateFilterOption, string> = {
  active: "Active",
  implemented: "Implemented",
  archived: "Archived",
  all: "All epics",
};

const STATE_SECTION_ORDER: EpicStateFilter[] = ["active", "implemented", "archived"];

const STATE_SECTION_LABELS: Record<EpicStateFilter, string> = {
  active: "Active",
  implemented: "Implemented",
  archived: "Archived",
};

const EPIC_SORT_OPTIONS: SortOption[] = [
  { value: 'createdAt', label: 'Newest first', direction: 'desc' },
  { value: 'createdAt', label: 'Oldest first', direction: 'asc' },
  { value: 'updatedAt', label: 'Last updated', direction: 'desc' },
  { value: 'name', label: 'Name A–Z', direction: 'asc' },
];

const SORT_PREFERENCE_KEY = 'epicsSortPreference';

export function EpicsPage() {
  usePageTitle("Epics");
  const isMobile = useIsMobile();

  const [stateFilter, setStateFilter] = useState<StateFilterOption>(() => {
    try {
      const stored = localStorage.getItem('epicsStateFilter');
      if (stored && (stored === 'active' || stored === 'implemented' || stored === 'archived' || stored === 'all')) {
        return stored as StateFilterOption;
      }
    } catch { /* privacy mode / quota */ }
    return 'active';
  });
  const handleStateFilterChange = useCallback((value: StateFilterOption) => {
    setStateFilter(value);
    setCursorStack([undefined]);
    try { localStorage.setItem('epicsStateFilter', value); } catch { /* quota/security */ }
  }, []);
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => {
    return (localStorage.getItem('epicsViewMode') as 'card' | 'list') || 'card';
  });
  
  // Sort state backed by localStorage
  const [sortState, setSortState] = useState(() =>
    getSortPreference(SORT_PREFERENCE_KEY, { orderBy: 'createdAt', orderDirection: 'desc' as const }, ['createdAt', 'updatedAt', 'name']),
  );

  // Page size backed by localStorage
  const [pageSize, setPageSize] = useState(() => {
    try {
      const stored = localStorage.getItem('epicsPageSize');
      const parsed = stored ? parseInt(stored, 10) : NaN;
      return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : 20;
    } catch { return 20; }
  });

  // Cursor stack for page-by-page navigation
  // Each entry is the cursor to fetch that page (undefined = first page)
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const currentPage = cursorStack.length;
  const currentCursor = cursorStack[cursorStack.length - 1];

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCursorStack([undefined]); // Reset to page 1
    try { localStorage.setItem('epicsPageSize', String(size)); } catch { /* quota/security */ }
  }, []);

  const handleSortChange = useCallback((orderBy: string, orderDirection: 'asc' | 'desc') => {
    const next = { orderBy, orderDirection };
    setSortState(next);
    setSortPreference(SORT_PREFERENCE_KEY, next);
    setCursorStack([undefined]); // Reset to page 1
  }, []);
  
  // Initialize scope from URL params (userId takes precedence over teamId) or localStorage
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTeamId = searchParams.get('teamId');
  const urlUserId = searchParams.get('userId');

  const [scope, setScope] = useState<ScopeMode>(() => {
    if (urlUserId) return 'user';
    if (urlTeamId) return 'team';
    const stored = localStorage.getItem('epicsScope');
    return (stored as ScopeMode) || 'my';
  });

  const [scopeId, setScopeId] = useState<string | undefined>(() => {
    if (urlUserId) return urlUserId;
    if (urlTeamId) return urlTeamId;
    const stored = localStorage.getItem('epicsScopeId');
    return stored || undefined;
  });

  const currentUser = useAuthStore((state) => state.user);

  // Sync scope when URL params change while mounted.
  // userId takes precedence over teamId; removing a param restores the persisted default.
  useEffect(() => {
    if (urlUserId) {
      if (urlUserId !== scopeId || scope !== 'user') {
        setScope('user');
        setScopeId(urlUserId);
        setCursorStack([undefined]);
      }
    } else if (urlTeamId && urlTeamId !== scopeId) {
      setScope('team');
      setScopeId(urlTeamId);
      setCursorStack([undefined]);
    } else if (!urlUserId && !urlTeamId && (scope === 'user' || scope === 'team') && scopeId) {
      // Both URL params removed — restore persisted default
      const stored = localStorage.getItem('epicsScope') as ScopeMode | null;
      setScope(stored || 'my');
      setScopeId(localStorage.getItem('epicsScopeId') || undefined);
      setCursorStack([undefined]);
    }
  }, [urlTeamId, urlUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScopeChange = (newScope: ScopeMode, newScopeId?: string) => {
    setScope(newScope);
    setScopeId(newScopeId);
    setCursorStack([undefined]); // Reset to page 1

    // Clear URL scope params if present
    let paramsChanged = false;
    if (searchParams.has('teamId')) {
      searchParams.delete('teamId');
      paramsChanged = true;
    }
    if (searchParams.has('userId')) {
      searchParams.delete('userId');
      paramsChanged = true;
    }
    if (paramsChanged) {
      setSearchParams(searchParams, { replace: true });
    }

    // Persist to localStorage
    localStorage.setItem('epicsScope', newScope);
    localStorage.setItem('epicsScopeId', newScopeId || '');
  };
  
  // Build filter parameters based on scope
  const epicFilters = useMemo(() => {
    const filters: EpicFilters = {
      orderBy: sortState.orderBy as EpicFilters['orderBy'],
      orderDirection: sortState.orderDirection,
      limit: pageSize,
      cursor: currentCursor,
    };

    if (stateFilter === 'all') {
      filters.includeArchived = true;
    } else {
      filters.state = stateFilter;
      if (stateFilter === 'archived') {
        filters.includeArchived = true;
      }
    }
    
    if (scope === 'my' && currentUser?.id) {
      filters.createdBy = currentUser.id;
    } else if (scope === 'team' && scopeId) {
      filters.teamId = scopeId;
    } else if (scope === 'user' && scopeId) {
      filters.createdBy = scopeId;
    }
    
    return filters;
  }, [scope, scopeId, stateFilter, currentUser?.id, sortState, pageSize, currentCursor]);
  
  const { data, isLoading, isFetching } = useEpicsPaged(epicFilters);

  const { data: teams } = useTeams();

  // Derive team hue when a specific team scope is selected
  const hueColor = useMemo(() => {
    if (scope === 'team' && scopeId && teams) {
      const team = teams.data.find((t) => t.id === scopeId);
      return team ? getTeamHueColor(team.name) : null;
    }
    return null;
  }, [scope, scopeId, teams]);

  const epics = data?.data ?? [];
  const totalCount = data?.meta?.totalCount;
  const nextCursor = data?.meta?.cursor;
  const hasNextPage = Boolean(data?.meta?.hasMore && nextCursor);
  const hasPreviousPage = currentPage > 1;
  const totalPages = totalCount != null ? Math.ceil(totalCount / pageSize) : undefined;

  // Group epics by state for the "All" view
  const groupedEpics = useMemo(() => {
    if (stateFilter !== 'all') return null;
    const groups = { active: [] as Epic[], implemented: [] as Epic[], archived: [] as Epic[] };
    for (const epic of epics) {
      const state = epic.state ?? (epic.isArchived ? 'archived' : 'active');
      if (state in groups) {
        groups[state as EpicStateFilter].push(epic);
      } else {
        groups.active.push(epic);
      }
    }
    return groups;
  }, [epics, stateFilter]);

  const handleNextPage = useCallback(() => {
    if (hasNextPage && nextCursor) {
      setCursorStack((prev) => [...prev, nextCursor]);
    }
  }, [hasNextPage, nextCursor]);

  const handlePreviousPage = useCallback(() => {
    setCursorStack((prev) => prev.length > 1 ? prev.slice(0, -1) : prev);
  }, []);

  return (
    <div className="relative">
      {/* Background hue glow when a team scope is selected */}
      {hueColor && (
        <div
          className="pointer-events-none fixed inset-0 z-0"
          style={{
            background: `radial-gradient(ellipse 140% 50% at 50% -5%, ${hueColor}2B, transparent 70%)`,
            backgroundColor: `${hueColor}09`,
            animation: 'glow-enter 0.5s ease-out forwards',
          }}
        />
      )}

      <div className="relative z-10 pt-4">
      <div className="glass-card rounded-2xl p-4 md:p-6">
      {/* Page title + Filters row on desktop */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex items-center gap-1">
          <h1 className="text-xl font-semibold">Epics</h1>
          <PageInfoButton title="Epics" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <EpicsScopeFilter
            scope={scope}
            {...(scopeId !== undefined && { scopeId })}
            onScopeChange={handleScopeChange}
          />
          <div data-help-id="epics.statusFilter">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                {STATE_FILTER_LABELS[stateFilter]}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(Object.keys(STATE_FILTER_LABELS) as StateFilterOption[]).map((option) => (
                <DropdownMenuItem key={option} onClick={() => handleStateFilterChange(option)}>
                  <Check className={`h-4 w-4 mr-2 ${stateFilter === option ? "opacity-100" : "opacity-0"}`} />
                  {STATE_FILTER_LABELS[option]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
          <SortControl
            sortOptions={EPIC_SORT_OPTIONS}
            activeSort={sortState}
            onSortChange={handleSortChange}
          />
          {!isMobile && (
            <div data-help-id="epics.viewToggle" className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-0.5">
              <button
                onClick={() => { setViewMode('card'); localStorage.setItem('epicsViewMode', 'card'); }}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'card' ? 'bg-white/[0.08] text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => { setViewMode('list'); localStorage.setItem('epicsViewMode', 'list'); }}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-white/[0.08] text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-lg bg-muted"
            />
          ))}
        </div>
      ) : epics.length === 0 ? (
        !totalCount && stateFilter === 'active' ? (
          <SmartEmptyState
            icon={Folder}
            title="No epics yet"
            description="Epics organize your work into features and tasks. Learn how to create one from an approved request."
            ctaLabel="Show me how"
            accent="emerald"
            helpFlowId="plan-epic"
          />
        ) : (
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <Folder className="h-16 w-16 text-muted-foreground/50" />
          </div>
          <p className="text-muted-foreground mb-4">
            {scope !== 'all' 
              ? "No epics found for this filter. Try changing your filter selection or creating a new epic."
              : stateFilter !== 'all' ? `No ${stateFilter} epics found` : "No epics yet"}
          </p>

        </div>
        )
      ) : (
        <>
          <div className="relative">
          {groupedEpics ? (
            // Grouped display when "All" filter is selected
            <div className="space-y-6">
              {STATE_SECTION_ORDER.map((sectionState) => {
                const sectionEpics = groupedEpics[sectionState];
                if (!sectionEpics || sectionEpics.length === 0) return null;
                return (
                  <div key={sectionState}>
                    <h2 className="text-sm font-medium text-muted-foreground mb-3">
                      {STATE_SECTION_LABELS[sectionState]}
                      <span className="ml-2 text-xs text-muted-foreground/60">{sectionEpics.length}</span>
                    </h2>
                    {viewMode === 'card' ? (
                      <div data-help-id="epics.cardGrid" className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {sectionEpics.map((epic) => (
                          <EpicCard key={epic.id} epic={epic} />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {sectionEpics.map((epic) => (
                          <EpicListRow key={epic.id} epic={epic} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : viewMode === 'card' ? (
            <div data-help-id="epics.cardGrid" className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {epics.map((epic) => (
                <EpicCard key={epic.id} epic={epic} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {epics.map((epic) => (
                <EpicListRow key={epic.id} epic={epic} />
              ))}
            </div>
          )}

          {/* Subtle loading overlay when navigating between pages */}
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-background/30 rounded-2xl pointer-events-none" />
          )}
          </div>

          {/* Pagination controls */}
          <PaginationControls
            showing={epics.length}
            totalCount={totalCount ?? undefined}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            currentPage={currentPage}
            totalPages={totalPages}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            isLoading={isFetching}
          />
        </>
      )}


      </div>
      </div>
    </div>
  );
}

// ─── List row for epics ────────────────────────────────────────────

function EpicListRow({ epic }: { epic: Epic }) {
  const hueColor = getTeamHueColor(epic.team?.name);

  const cleanDesc = epic.description
    ?.replace(/^#+\s+/gm, '')
    .replace(/\n/g, ' ')
    .trim();

  const lastEditedRaw = epic.updatedAt ? new Date(epic.updatedAt) : new Date(epic.createdAt);
  const hoursAgo = Math.floor((Date.now() - lastEditedRaw.getTime()) / 3_600_000);
  const lastEdited = hoursAgo < 1
    ? "just now"
    : hoursAgo < 24
      ? `${hoursAgo}h ago`
      : `${Math.floor(hoursAgo / 24)}d ago`;

  const teamKey = epic.team?.key ?? "—";
  const totalFeatures = epic._count?.features ?? 0;
  const completedFeatures = epic.completedFeatureCount ?? 0;
  const progressPct = totalFeatures > 0 ? Math.round((completedFeatures / totalFeatures) * 100) : 0;

  return (
    <Link
      to={`/epics/${epic.id}`}
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.04] transition-colors group"
    >
      {/* Icon */}
      <div className="shrink-0 h-8 w-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${hueColor}18` }}>
        <Folder className="h-4 w-4" style={{ color: hueColor }} />
      </div>

      {/* Name + description */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {epic.identifier && (
            <span className="text-xs text-muted-foreground font-mono mr-2">{epic.identifier}</span>
          )}
          {epic.name}
        </p>
        {cleanDesc && (
          <p className="text-xs text-muted-foreground truncate">{cleanDesc}</p>
        )}
      </div>

      {/* Team badge */}
      {epic.team ? (
        <span className="shrink-0 text-xs text-muted-foreground bg-white/[0.04] border border-white/[0.06] rounded-full px-2 py-0.5">
          {teamKey}
        </span>
      ) : (
        <span className="shrink-0 flex items-center gap-1 text-xs text-muted-foreground bg-white/[0.04] border border-white/[0.06] rounded-full px-2 py-0.5">
          <User className="h-3 w-3" />
          Personal
        </span>
      )}

      {/* Progress */}
      <div className="shrink-0 w-16">
        <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${String(progressPct)}%`, backgroundColor: hueColor }} />
        </div>
      </div>

      {/* Creator avatar */}
      {epic.creator?.avatarUrl ? (
        <img
          src={epic.creator.avatarUrl}
          alt={epic.creator.name}
          className="shrink-0 h-6 w-6 rounded-full"
        />
      ) : epic.creator?.name ? (
        <div className="shrink-0 h-6 w-6 rounded-full bg-white/[0.08] flex items-center justify-center text-xs font-medium text-muted-foreground">
          {getInitials(epic.creator.name)}
        </div>
      ) : null}

      {/* Edited time */}
      <span className="shrink-0 text-xs text-muted-foreground w-12 text-right tabular-nums">
        {lastEdited}
      </span>
    </Link>
  );
}
